import { useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Plus, Trash2, Loader2, Check, X, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../lib/api';

interface LeaveType {
  id: string;
  name: string;
  code?: string;
  days_allowed: number;
  paid?: boolean;
  carry_forward?: boolean;
  max_carry_forward?: number;
}

/**
 * Real CRUD for people.leave_types (the master catalogue), replacing the old
 * local-only "quotas" form. HR/Admin can add, rename, change the default days,
 * toggle paid / carry-forward, and remove a type. Employees never reach this
 * screen, and the API rejects non-HR writes anyway.
 */
export function LeaveTypesManager() {
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<LeaveType>>({});
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState<{ name: string; days_allowed: number; paid: boolean; carry_forward: boolean }>(
    { name: '', days_allowed: 0, paid: true, carry_forward: false }
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/people/leave/types');
      setTypes(res.data?.data ?? res.data ?? []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load leave types');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (t: LeaveType) => { setEditId(t.id); setDraft({ ...t }); };
  const cancelEdit = () => { setEditId(null); setDraft({}); };

  const saveEdit = async (id: string) => {
    const name = String(draft.name ?? '').trim();
    if (!name) { toast.error('Name is required'); return; }
    if (Number(draft.days_allowed) < 0) { toast.error('Days must be 0 or more'); return; }
    setSavingId(id);
    try {
      const res = await apiClient.patch(`/people/leave/types/${id}`, {
        name,
        daysAllowed: Number(draft.days_allowed) || 0,
        paid: !!draft.paid,
        carryForward: !!draft.carry_forward,
      });
      const updated = res.data?.data ?? res.data;
      setTypes((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast.success(`Saved ${updated.name}`);
      cancelEdit();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save');
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (t: LeaveType) => {
    if (!window.confirm(`Remove "${t.name}"? Existing balances keep their history, but it can no longer be requested.`)) return;
    setSavingId(t.id);
    try {
      await apiClient.delete(`/people/leave/types/${t.id}`);
      setTypes((prev) => prev.filter((x) => x.id !== t.id));
      toast.success(`Removed ${t.name}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not remove');
    } finally {
      setSavingId(null);
    }
  };

  const addType = async () => {
    const name = newType.name.trim();
    if (!name) { toast.error('Name is required'); return; }
    setSavingId('new');
    try {
      const res = await apiClient.post('/people/leave/types', {
        name,
        daysAllowed: Number(newType.days_allowed) || 0,
        paid: newType.paid,
        carryForward: newType.carry_forward,
      });
      const created = res.data?.data ?? res.data;
      setTypes((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`Added ${created.name}`);
      setAdding(false);
      setNewType({ name: '', days_allowed: 0, paid: true, carry_forward: false });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not add leave type');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading leave types…</div>;
  }

  return (
    <div className="space-y-3">
      {types.map((t) => {
        const editing = editId === t.id;
        return (
          <div key={t.id} className="flex flex-wrap items-center gap-3 border-b border-border pb-3">
            {editing ? (
              <>
                <Input className="w-44" value={draft.name ?? ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Name" />
                <div className="flex items-center gap-1.5">
                  <Input type="number" className="w-20" value={draft.days_allowed ?? 0} onChange={(e) => setDraft((d) => ({ ...d, days_allowed: Number(e.target.value) }))} />
                  <span className="text-xs text-muted-foreground">days/yr</span>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input type="checkbox" className="accent-primary" checked={!!draft.paid} onChange={(e) => setDraft((d) => ({ ...d, paid: e.target.checked }))} /> Paid
                </label>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input type="checkbox" className="accent-primary" checked={!!draft.carry_forward} onChange={(e) => setDraft((d) => ({ ...d, carry_forward: e.target.checked }))} /> Carry forward
                </label>
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" variant="primary" disabled={savingId === t.id} onClick={() => saveEdit(t.id)}>
                    {savingId === t.id ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit}><X className="size-4" /></Button>
                </div>
              </>
            ) : (
              <>
                <div className="min-w-[160px]">
                  <p className="font-medium text-sm text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.days_allowed} days/year · {t.paid === false ? 'Unpaid' : 'Paid'}{t.carry_forward ? ' · Carry forward' : ''}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(t)} title="Edit"><Pencil className="size-4" /></Button>
                  <Button size="sm" variant="ghost" disabled={savingId === t.id} onClick={() => remove(t)} title="Remove">
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </>
            )}
          </div>
        );
      })}

      {adding ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border p-3">
          <Input className="w-44" autoFocus value={newType.name} onChange={(e) => setNewType((n) => ({ ...n, name: e.target.value }))} placeholder="Leave type name" />
          <div className="flex items-center gap-1.5">
            <Input type="number" className="w-20" value={newType.days_allowed} onChange={(e) => setNewType((n) => ({ ...n, days_allowed: Number(e.target.value) }))} />
            <span className="text-xs text-muted-foreground">days/yr</span>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" className="accent-primary" checked={newType.paid} onChange={(e) => setNewType((n) => ({ ...n, paid: e.target.checked }))} /> Paid
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" className="accent-primary" checked={newType.carry_forward} onChange={(e) => setNewType((n) => ({ ...n, carry_forward: e.target.checked }))} /> Carry forward
          </label>
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="primary" disabled={savingId === 'new'} onClick={addType}>
              {savingId === 'new' ? <Loader2 className="size-4 animate-spin" /> : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}><X className="size-4" /></Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" className="w-full mt-2" onClick={() => setAdding(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add Leave Type
        </Button>
      )}
    </div>
  );
}
